import { isTV } from "./platform";

// Loaded by index.html when running on webOS (see ./webOSTVjs-1.2.10/webOSTV.js).
// `service.request` issues a Luna service call to the TV; with `subscribe: true`
// the onSuccess callback fires on every update from the service.
type WebOSScreenSaverMessage = {
  state?: string;
  timestamp?: number;
};
type WebOSServiceRequest = (
  uri: string,
  options: {
    method?: string;
    parameters?: Record<string, unknown>;
    onSuccess?: (response: WebOSScreenSaverMessage) => void;
    onFailure?: (response: WebOSScreenSaverMessage) => void;
    subscribe?: boolean;
    resubscribe?: boolean;
  },
) => { cancel: () => void };

declare global {
  interface Window {
    webOS?: {
      service?: { request: WebOSServiceRequest };
      deviceInfo?: (
        callback: (info: {
          modelName?: string;
          screenWidth?: number;
          screenHeight?: number;
          // "true" on a 4K/UHD panel even when the UI renders at 1080.
          uhd?: boolean | string;
        }) => void,
      ) => void;
    };
  }
}

const WEBOS_CLIENT_NAME = "com.onmo.plus";
const TV_POWER_URI = "luna://com.webos.service.tvpower/power";

const log = (...args: unknown[]) => console.log("[wakeLock]", ...args);

/**
 * Subscribe to webOS's screensaver-about-to-activate event and respond with
 * `ack: false` to veto activation. This is the only mechanism that actually
 * defeats the TV's screen-off timer on webOS — Wake Lock API, media playback,
 * and gamepad input do not reset it (only IR remote presses do).
 */
const blockWebOSScreenSaver = () => {
  const request = window.webOS?.service?.request;
  if (!request) {
    log("webOS.service not available");
    return;
  }
  request(TV_POWER_URI, {
    method: "registerScreenSaverRequest",
    parameters: { subscribe: true, clientName: WEBOS_CLIENT_NAME },
    subscribe: true,
    resubscribe: true,
    onSuccess: (msg) => {
      log("screensaver event:", msg.state);
      if (msg.state !== "Active") return;
      request(TV_POWER_URI, {
        method: "responseScreenSaverRequest",
        parameters: {
          clientName: WEBOS_CLIENT_NAME,
          ack: false,
          timestamp: msg.timestamp,
        },
      });
    },
    onFailure: (e) =>
      console.warn("[wakeLock] screensaver subscription failed:", e),
  });
  log("registered webOS screensaver blocker");
};

/**
 * Keep the screen awake on TV platforms by vetoing webOS's screensaver via
 * the Luna service. No-op on non-TV platforms and on TVs without webOS.
 */
export const initWakeLock = () => {
  if (!isTV) {
    log("init skipped (not TV)");
    return;
  }
  log("init", {
    hasWebOS: !!window.webOS?.service,
    userAgent: navigator.userAgent,
  });
  blockWebOSScreenSaver();
};
